import type { ComponentType } from 'react';
import { makeStyles } from 'tss-react/mui';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DataObjectIcon from '@mui/icons-material/DataObject';
import BuildIcon from '@mui/icons-material/Build';
import { Page, Header, Content, Link } from '@backstage/core-components';

interface ToolItem {
  title: string;
  description: string;
  to: string;
}

interface ToolGroup {
  title: string;
  description?: string;
  Icon: ComponentType<{ style?: React.CSSProperties }>;
  tools: ToolItem[];
}

const TOOL_GROUPS: ToolGroup[] = [
  {
    title: 'Markdown',
    description: 'Author and convert Markdown content',
    Icon: EditNoteIcon,
    tools: [
      {
        title: 'Markdown Editor',
        description: 'Live split-pane editor with rendered preview',
        to: '/tools/markdown/editor',
      },
      {
        title: 'Markdown ↔ HTML',
        description: 'Convert between Markdown and HTML in either direction',
        to: '/tools/markdown/converter',
      },
    ],
  },
  {
    title: 'Data & Encoding',
    description: 'Encode, decode, convert and format structured data',
    Icon: DataObjectIcon,
    tools: [
      {
        title: 'Base64 Encode / Decode',
        description: 'Encode strings to Base64 or decode Base64 back to text',
        to: '/tools/data/base64',
      },
      {
        title: 'JSON ↔ YAML',
        description: 'Convert between JSON and YAML formats',
        to: '/tools/data/json-yaml',
      },
      {
        title: 'JSON ↔ CSV',
        description: 'Convert between JSON arrays and CSV',
        to: '/tools/data/json-csv',
      },
      {
        title: 'JWT Decoder',
        description: 'Inspect a JWT token — header, payload and expiry status',
        to: '/tools/data/jwt',
      },
    ],
  },
  {
    title: 'Generators & Utilities',
    description: 'Quick generators and everyday developer utilities',
    Icon: BuildIcon,
    tools: [
      {
        title: 'UUID Generator',
        description: 'Generate RFC 4122 v4 UUIDs in bulk, copy with one click',
        to: '/tools/misc/uuid',
      },
      {
        title: 'Password / Token Generator',
        description: 'Generate cryptographically random passwords with custom rules',
        to: '/tools/misc/password',
      },
      {
        title: 'Cron Parser',
        description: 'Translate a cron expression into plain English',
        to: '/tools/misc/cron',
      },
    ],
  },
];

const useStyles = makeStyles()((theme) => ({
  section: { marginBottom: theme.spacing(5) },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2.5),
    paddingBottom: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  sectionIcon: {
    width: 38,
    height: 38,
    borderRadius: '50%',
    backgroundColor:
      theme.palette.mode === 'dark' ? 'rgba(244,121,32,0.22)' : '#FDE8F5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  sectionTitle: { fontWeight: 700, fontSize: 17 },
  sectionDesc: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginTop: 3,
  },
  toolCard: {
    height: '100%',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    transition: 'box-shadow 0.18s, transform 0.14s',
    '&:hover': { boxShadow: theme.shadows[4], transform: 'translateY(-2px)' },
  },
  toolCardContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
  },
  toolCardText: { flex: 1 },
  toolCardTitle: { fontWeight: 700, fontSize: 14 },
  toolCardDesc: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
}));

function ToolGroupSection({ group }: { group: ToolGroup }) {
  const { classes } = useStyles();
  const { Icon } = group;
  return (
    <div className={classes.section}>
      <div className={classes.sectionHeader}>
        <div className={classes.sectionIcon}>
          <Icon style={{ fontSize: 20, color: '#E6007E' }} />
        </div>
        <div>
          <Typography className={classes.sectionTitle}>{group.title}</Typography>
          {group.description && (
            <Typography className={classes.sectionDesc}>
              {group.description}
            </Typography>
          )}
        </div>
      </div>
      <Grid container spacing={2}>
        {group.tools.map(tool => (
          <Grid key={tool.to} item xs={12} sm={6} md={4}>
            <Card className={classes.toolCard} elevation={0}>
              <CardActionArea
                component={Link}
                to={tool.to}
                style={{ display: 'block', textDecoration: 'none' }}
              >
                <CardContent className={classes.toolCardContent}>
                  <div className={classes.toolCardText}>
                    <Typography className={classes.toolCardTitle}>
                      {tool.title}
                    </Typography>
                    <Typography className={classes.toolCardDesc}>
                      {tool.description}
                    </Typography>
                  </div>
                  <ChevronRightIcon
                    style={{ fontSize: 20, opacity: 0.5, flexShrink: 0 }}
                  />
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </div>
  );
}

export function ToolsHubPage() {
  return (
    <Page themeId="tool">
      <Header
        title="Developer Tools"
        subtitle="Client-side utilities for everyday development tasks"
      />
      <Content>
        {TOOL_GROUPS.map(group => (
          <ToolGroupSection key={group.title} group={group} />
        ))}
      </Content>
    </Page>
  );
}
